import { Injectable } from '@angular/core';
import { Quote } from './quote-obj';
import { DepthFetchCount, QUOTE_UPDATE_TYPE, SOCKET_MSG_TYPES } from '../websocket/websocket.enums';
import { Subject } from 'rxjs';
import { QuoteSocketUpdateI, LTPClosePriceSocketUpdate, OIDataI, DeltaSocketUpdate, LTPAndClosePiceObj, LTPClosePiceObj, SocketDepth, DPRDataI } from '../websocket/websocket.interfaces';
import { MarketIDMap } from '@app/_shared/shared.enums';
import { DEFAULT_PRECISION, MOMENT_TIME_FORMAT, Utils } from '@app/_helpers';
import { SymbolSearchService } from '../symbol-search';
import { OhlcDataResponse } from '../../../../utProto/chartingData_pb';
import { OHLCDataI } from '@app/app.interface';
import { DeltaResponse } from '../../../../utProto/marketData_pb';
import { GreeksRequest, GreeksResponse } from '@app/modules/trading/modules/pay-off-curve/modules/rapid/rapid-interfaces';
import { HttpClient } from '@angular/common/http';
import { ConfigurationService } from '../configuration/configuration.service';
import { EXCHANGE_STRINGS } from '@app/_shared/modules/portfolio-form/portfolio-form.enums';
import { MarketKeywords } from '@app/app.structs';
import * as moment from 'moment';
import { TimerConfig } from '@app/modules/admin/providers/timer-configuration/timer-configuration.models';
import { S } from '@angular/cdk/keycodes';

@Injectable({
  providedIn: 'root'
})
export class QuoteStoreService {

  // key is exchangeId-securityId and value is symbolId and scripPrecision
  quotes = new Map<string, Quote>();
  private quoteUpdateSubject = new Map<QUOTE_UPDATE_TYPE, Subject<any>>();
  public symbolPrecMap: Map<string, { symbolId: number, scripPrecision: number }> = new Map();
  public ohlcSymbolMap: Map<number, OHLCDataI> = new Map();
  private ltpAndCloseMap: Map<string, LTPAndClosePiceObj> = new Map(); // key is marketId_securityId and value is ltpandcloseprice data
  private depthMap: Map<string, Map<DepthFetchCount,SocketDepth>> = new Map();
  private topDepthMap: Map<string, SocketDepth> = new Map();
  private dprMap: Map<string, DPRDataI> = new Map();

  private oiMap: Map<string, OIDataI> = new Map();

  timeConfiguration: TimerConfig[];


  constructor(
    private symbolSearchService: SymbolSearchService,
    private http: HttpClient,
    private conf: ConfigurationService
  ) { }

  getFeedSubject<T = any>(type: QUOTE_UPDATE_TYPE) {
    if (!this.quoteUpdateSubject.has(type)) {
      this.quoteUpdateSubject.set(type, new Subject<T>());
    }
    return this.quoteUpdateSubject.get(type) as Subject<T>;
  }

  fetchIV(request: GreeksRequest) {
    const url = this.conf.applicationUrls.getIV;
    return this.http.post(url, request);
  }

  fetchGreeks(request: GreeksRequest) {

    const url = this.conf.applicationUrls.getGreeks;

    return this.http.post<GreeksResponse[]>(url, request);
  }

  processIndexQuoteUpdate(data: any, index?: number) {
    /**
     * msgLen(0-4) + msgType(4-8) + iscompreeess(8) + exchangeId(9-11) +
     * securityId(11-15) + High(15-19) + Low(19-23) + Open(23-27) +
     * close(27-31) + value(31-35) + ID(35-59) + LastTradeTime(59-63)
     */
    const quoteStruct = new DataView(data);
    const symbolName = new Uint8Array(data.slice(35, 59));
    const decoder = new TextDecoder();
    const exchangeID = quoteStruct.getUint16(index + 9, true);
    const securityID = quoteStruct.getUint32(index + 11, true);
    const indData: QuoteSocketUpdateI = {
      ExchangeId: Object.keys(MarketIDMap).find(key => MarketIDMap[key] === exchangeID),
      SecurityId: securityID,
      HighPrice: quoteStruct.getUint32(index + 15, true),
      LowPrice: quoteStruct.getUint32(index + 19, true),
      OpenPrice: quoteStruct.getUint32(index + 23, true),
      ClosePrice: quoteStruct.getUint32(index + 27, true),
      Value: quoteStruct.getUint32(index + 31, true),
      LastTradeTime: quoteStruct.getUint32(index + 59, true)
    };
    const indicesKey = MarketIDMap[exchangeID] + '_' + securityID;

    /**
     * if indiceClosePrice Map is having close price then 
     * picking close price from it instead of using close price from feed
     */
    if (this.symbolSearchService.indicesClosePriceMap.has(indicesKey)
      && this.symbolSearchService.indicesClosePriceMap.get(indicesKey)[0]) {
      indData.ClosePrice = this.symbolSearchService.indicesClosePriceMap.get(indicesKey)[0]
    }

    this.processIndexQuote(indData)
    const key = this.getMapKey(exchangeID, securityID)
    if (!this.quotes.has(key)) {
      this.quotes.set(key, new Quote(indData));
    } else {
     
    const pickClosePriceInLTP = this.checkToPickClosePrice(indData.ExchangeId, indData.LastTradePrice);
    this.quotes.get(key).updateQuote(indData, pickClosePriceInLTP);

    }

    this.getFeedSubject(QUOTE_UPDATE_TYPE.INDEX).next(indData);
    return quoteStruct.getUint32(index);
  }

  processGroupQuote(data: ArrayBuffer) {
    /**
     * group quote contains multiple quote data.
     * The base header is: msgLen(0-4) + msgType(4-8) + iscompressed(8)
     * remaining data starts from 9 byte.
     * Again in remaining data we have base header to know the msgtype and length of the quote data.
     */
    const remData = data.slice(9);
    const quotStruct = new DataView(remData);
    const msgType = quotStruct.getUint32(4);
    if (msgType === SOCKET_MSG_TYPES.FIFTY_DEPTH) {
      this.process50DepthUpdate(remData, 0);
    } else {
      let i = 0;
      while (i < remData.byteLength) {
        const msgType = quotStruct.getUint32(i + 4);
        
        if (msgType === SOCKET_MSG_TYPES.LTP_Close_PRICE) {
          i = i + this.processLtpAndClose(remData, i);
        } else {
          // slicing individual quotes and processing them separately
          const quoteLength = quotStruct.getUint32(i);
          i = i + this.processQuoteUpdate(remData, i);
        }
      }
    }
  }

  processDPR(data, index: number = 0) {
    /**
     * msgLen(0-4) + msgType(4-8) + iscompressed(8) + exchangeId(9-11) +
     * securityId(11-15) + LowerPrice(15-19) + UpperPrice(19-23)
     */
    const dprStruct = new DataView(data);
    const exchangeID = dprStruct.getUint16(index + 9, true);
    const securityID = dprStruct.getUint32(index + 11, true);
    let scrip_precision;
    let symbolId;
    if (this.symbolPrecMap.has(this.getMapKey(exchangeID, securityID))) {
        scrip_precision = this.symbolPrecMap.get(this.getMapKey(exchangeID, securityID)).scripPrecision;
        symbolId = this.symbolPrecMap.get(this.getMapKey(exchangeID, securityID)).symbolId;
    }
    const dprData: DPRDataI = {
        market_id: MarketIDMap[exchangeID],
        security_id: securityID,
        symbol_id: symbolId,
        lower_ckt_limit: Utils.applyPrecision(dprStruct.getInt32(index + 15, true), scrip_precision),
        upper_ckt_limit: Utils.applyPrecision(dprStruct.getInt32(index + 19, true), scrip_precision)
    };

    this.dprMap.set(Utils.getJoinedString(exchangeID, dprData.security_id), dprData);
    this.getFeedSubject(QUOTE_UPDATE_TYPE.DPR).next(dprData);
    return dprStruct.getUint32(index);
}


  process50DepthUpdate(data: ArrayBuffer, index: number = 0): number {
    const quoteStruct = new DataView(data);
    const tbtLength = 1615;
    const exchangeID = quoteStruct.getUint16(index + 9, true);  // true for little endian
    const securityID = quoteStruct.getUint32(index + 11, true);

    // TODO: get precision from symbol info map
    const precision = 
      DEFAULT_PRECISION;

    let depthObj: SocketDepth = {
      buy: [],
      sell: [],
      symbol_id: Utils.getJoinedString(exchangeID, securityID)
    };

    depthObj = Utils.convertDepth(
      data.slice(index + 15, index + tbtLength),
      precision,
      Utils.getJoinedString(exchangeID, securityID)
    );

    this.getFeedSubject<SocketDepth>(QUOTE_UPDATE_TYPE.FIFTY_DEPTH).next(depthObj);

    return quoteStruct.getUint32(index);
  }

  processQuoteUpdate(quote: any, index?: number) {
    /**
    * quote = msgLen(0-4) + msgType(4-8) + isCompressed(8) + ExchangeId(9-11) +
    * SecurityId(11-15) + Volume(15-23) + Value(23-31) + (tradeflag + trade)(32) +
    * LastTradeQty(32-36) + LastTradePrice(36-40) + LastTradeTime(40-44) + OpenPrice(44-48) +
    * ClosePrice(48-52) + HighPrice(52-56) + LowPrice(56-60) + TotalBidQty(60-64) + TotaAskQty(64-68) +
    * AvgTradePrice(68-72) + LastUpdateTime(72-76)
    */
    const quoteLength = 236;
    const topDepthLength = 108;

    const quoteStruct = new DataView(quote);
    const exchangeID = quoteStruct.getUint16(index + 9, true);
    const securityID = quoteStruct.getUint32(index + 11, true);
    const msgType = quoteStruct.getUint32(index + 4);
    const quoteData: QuoteSocketUpdateI = {
      ExchangeId: MarketIDMap[exchangeID],
      SecurityId: securityID,
      Volume: Utils.getUint64(quoteStruct, index + 15, true),
      Value: Utils.getUint64(quoteStruct, index + 23, true),
      LastTradeQty: quoteStruct.getInt32(index + 32, true),
      LastTradePrice: quoteStruct.getInt32(index + 36, true),
      LastTradeTime: quoteStruct.getUint32(index + 40, true),
      OpenPrice: quoteStruct.getUint32(index + 44, true),
      ClosePrice: quoteStruct.getUint32(index + 48, true),
      HighPrice: quoteStruct.getUint32(index + 52, true),
      LowPrice: quoteStruct.getUint32(index + 56, true),
      TotalBidQty: quoteStruct.getUint32(index + 60, true),
      TotalAskQty: quoteStruct.getUint32(index + 64, true),
      AvgTradePrice: quoteStruct.getUint32(index + 68, true),
      LastUpdateTime: quoteStruct.getUint32(index + 72, true),
    };

    const key = this.getMapKey(exchangeID, securityID)

    /**
     * if symbolClosePrice Map is having close price then 
     * picking close price from it instead of using close price from feed
     * in symbolinfo map against a key we have array has value, in which 
     * index 0 is market lot
     * index 1 is tick size
     * index 2 is scrip precision
     * index 3 is close price
     */
    if (this.symbolSearchService.symbolInfoMap.has(key)
      && this.symbolSearchService.symbolInfoMap.get(key)[3]) {
      quoteData.ClosePrice = this.symbolSearchService.symbolInfoMap.get(key)[3]
    }

    if (!this.quotes.has(key)) {
      this.quotes.set(key, new Quote(quoteData));
    } else {  
      const pickClosePriceInLTP = this.checkToPickClosePrice(quoteData.ExchangeId, quoteData.LastTradePrice);
      this.quotes.get(key).updateQuote(quoteData, pickClosePriceInLTP);
    }
    this.getFeedSubject(QUOTE_UPDATE_TYPE.QUOTE).next(quoteData);
    this.getFeedSubject(QUOTE_UPDATE_TYPE.THROTTLER_LTP).next(key);

    let depthObj: SocketDepth = { buy: [], sell: [], symbol_id: Utils.getJoinedString(exchangeID, securityID) };
        // tslint:disable-next-line:max-line-length
        depthObj = Utils.convertDepth(quote.slice(index+76,index + (msgType === SOCKET_MSG_TYPES.QUOTE ? quoteLength : topDepthLength)), DEFAULT_PRECISION, Utils.getJoinedString(exchangeID, securityID));
        if (depthObj.buy.length === 1) {
            this.topDepthMap.set(Utils.getJoinedString(exchangeID, securityID), depthObj);
        } else {
            let depthFetchMap : Map<DepthFetchCount,SocketDepth> = this.depthMap.get(Utils.getJoinedString(exchangeID, securityID)) || new Map();
            depthFetchMap.set(DepthFetchCount.FIVE, depthObj);
            this.depthMap.set(Utils.getJoinedString(exchangeID, securityID), depthFetchMap);
            this.getFeedSubject<SocketDepth>(QUOTE_UPDATE_TYPE.DEPTH).next(depthObj);
        }
    
    return quoteStruct.getUint32(index);
  }

  processLtpAndClose(data, index?: number) {
    /**
     * msgLen(0-4) + msgType(4-8) + isCompressed(8) + ExchangeId(9-11) +
     * SecurityId(11-15) + ClosePrice(15-19) + Ltp(19-23)
     */
    const ltpCloseStruct = new DataView(data);
    const exchangeID = ltpCloseStruct.getUint16(index + 9, true);
    const securityID = ltpCloseStruct.getUint32(index + 11, true);

    const ltpCloseData: LTPClosePriceSocketUpdate = {
      SecurityId: securityID,
      ExchangeId: MarketIDMap[exchangeID],
      ClosePrice: ltpCloseStruct.getUint32(index + 15, true),
      Ltp: ltpCloseStruct.getUint32(index + 19, true),
    };
    const key = this.getMapKey(exchangeID, securityID);
    /**
      * if symbolClosePrice Map is having close price then 
      * picking close price from it instead of using close price from feed
      * in symbolinfo map against a key we have array has value, in which 
      * index 0 is market lot
      * index 1 is tick size
      * index 2 is scrip precision
      * index 3 is close price
      */
    if (this.symbolSearchService.symbolInfoMap.has(key)
      && this.symbolSearchService.symbolInfoMap.get(key)[3]) {
      ltpCloseData.ClosePrice = this.symbolSearchService.symbolInfoMap.get(key)[3]
    }

    if (!this.quotes.has(key)) {
      this.quotes.set(key, new Quote());
    }
 
    const pickClosePriceInLTP =  this.checkToPickClosePrice(ltpCloseData.ExchangeId, ltpCloseData.Ltp); 
    this.quotes.get(key).updateLTPCloseOnly(ltpCloseData, pickClosePriceInLTP);

    this.getFeedSubject(QUOTE_UPDATE_TYPE.LTPClose).next(ltpCloseData);
    this.getFeedSubject(QUOTE_UPDATE_TYPE.THROTTLER_LTP).next(key);
    /**
     * sending msglen + 4 because from mdl 
     * two times msglen comes one is having value as 23(len of msglen is included) and second one is having 
     * 19(len of msglen is  not included), so currently we are reading second msglen. Adding + 4 because
     * entire LTP data is of 23 bytes
     */
    return ltpCloseStruct.getUint32(index) + 4;

  }

  processOHLC(data: OhlcDataResponse) {
    let scrip_precision;
    let trade_value;
    let market_lot;
    let market_id;
    if (this.ohlcSymbolMap.has(data.getSymbolId())) {
      scrip_precision = this.ohlcSymbolMap.get(data.getSymbolId()).scrip_precision;
      trade_value = this.ohlcSymbolMap.get(data.getSymbolId()).trade_value;
      market_lot = this.ohlcSymbolMap.get(data.getSymbolId()).market_lot;
      market_id = this.ohlcSymbolMap.get(data.getSymbolId()).market_id;
    }
    const ohlcData = Utils.convertOHLC(data, scrip_precision, trade_value, market_lot, market_id, data.getSymbolId());
    const resData = {
      data: ohlcData,
      responseId: data.getResponseid()
    };

    this.getFeedSubject(QUOTE_UPDATE_TYPE.OHLC).next(resData);
  }

  processOIUpdates(data, index: number) {
    /**
     * msgLen(0-4) + msgType(4-8) + isCompressed(8) + ExchangeId(9-11) +
     * SecurityId(11-15) + OIQty(15-19) + OIValue(19-23) + OIChange(23-27)
     */
    const OIStruct = new DataView(data);
    const exchangeID = OIStruct.getUint16(index + 9, true);
    const securityID = OIStruct.getUint32(index + 11, true);
    const OIData: OIDataI = {
      security_id: securityID,
      oi_qty: OIStruct.getUint32(index + 15, true),
      oi_value: OIStruct.getUint32(index + 19, true),
      oi_change: OIStruct.getInt32(index + 23, true),
      exchange: exchangeID
    };
    this.oiMap.set(this.getMapKey(exchangeID, securityID), OIData);
    this.getFeedSubject(QUOTE_UPDATE_TYPE.OPEN_INTEREST).next(OIData);
    /**
     * sending msglen + 4 because from mdl 
     * two times msglen comes one is having value as 27(len of msglen is included) and second one is having 
     * 23(len of msglen is  not included), so currently we are reading second msglen. Adding + 4 because
     * entire OIdata is of 27 bytes
     */
    return OIStruct.getUint32(index) + 4;
  }

  processClosePrice(data, symbolId, index?: number) {


    const ltpCloseStruct = new DataView(data);
    const symbolInfo = Utils.extractFromSymbolId(symbolId);
    const exchangeID = symbolInfo[1];
    const securityID = symbolInfo[3];

    const closeData: LTPClosePriceSocketUpdate = {
      SecurityId: securityID,
      ExchangeId: MarketIDMap[exchangeID],
      ClosePrice: ltpCloseStruct.getUint32(index, true),
      Ltp: 0,
    };
    
    const key = this.getMapKey(exchangeID, securityID);

    if (this.symbolSearchService.symbolInfoMap.has(key)
      && this.symbolSearchService.symbolInfoMap.get(key)[3]) {
      closeData.ClosePrice = this.symbolSearchService.symbolInfoMap.get(key)[3]
    } 

    if (!this.quotes.has(key)) {
      this.quotes.set(key, new Quote());
    }

    const pickClosePriceInLTP = this.checkToPickClosePrice(closeData.ExchangeId, closeData.Ltp);
    this.quotes.get(key).updateLTPCloseOnly(closeData, pickClosePriceInLTP);
    this.getFeedSubject(QUOTE_UPDATE_TYPE.LTPClose).next(closeData);
    return ltpCloseStruct.getUint32(index);
  }


  /**
   * Get string used as key for map containing quote objects.
   *
   * @private
   * @param {number} marketID Exchange ID of symbol.
   * @param {number} securityId Security ID of symbol.
   * @returns String used as key
   * @memberof QuoteStoreService
   */
  getMapKey(marketID: number, securityId: number) {
    const key = `${marketID}_${securityId}`;
    return key;
  }

  /**
   * Get Quote object of that symbol.
   *
   * @param {number} marketId
   * @param {number} securityID
   * @returns {Quote} Quote Object
   * @memberof QuoteStoreService
   */
  getFeedObj(marketId: number, securityID: number, market_security_id?: string) {
    const key = market_security_id || this.getMapKey(marketId, securityID);
    if (!this.quotes.has(key)) {
      this.quotes.set(key, new Quote());
      this.quotes.get(key).marketId = MarketIDMap[marketId];
      this.quotes.get(key).securityId = securityID;
    }

    return this.quotes.get(key);
  }

  getOIObj(key: string): any {
    if (this.oiMap.has(key)) {
      return this.oiMap.get(key);
    }
    return {};
  }

  getDepthObj(symbolId: string, depthCount: DepthFetchCount = DepthFetchCount.FIVE): any {
    if (this.depthMap.has(symbolId) && this.depthMap.get(symbolId).has(depthCount)) {
      return this.depthMap.get(symbolId).get(depthCount);
    }
    return {};
  }

  getDPRObj(key: string): any {
    if (this.dprMap.has(key)) {
      return this.dprMap.get(key);
    }
    return {};
  }

  getTopDepthObj(symbolId: string) {
    if (this.topDepthMap.has(symbolId)) {
      return this.topDepthMap.get(symbolId);
    }
    return {};
  }

//   getIndicative(marketId: string, securityId: number) {
//     const key = this.getMapKeyStr(marketId, securityId);
//     if(this.indicativeClosePriceMap.has(key)) {
//         return this.indicativeClosePriceMap.get(key);
//     }
//     return null;
// }


  private processIndexQuote(indexQuote: QuoteSocketUpdateI) {
    indexQuote.LastTradePrice = indexQuote.Value;
    indexQuote.Value = 0;
    indexQuote.Volume = 0;
    this.getFeedSubject(QUOTE_UPDATE_TYPE.INDEX).next(indexQuote);
  }

  processDeltaResponse(deltaResponse: DeltaResponse) {
    const deltaData: DeltaSocketUpdate = {
      delta: deltaResponse.getDelta(),
      securityId: deltaResponse.getOptionSecurityId(),
      exchangeId: MarketIDMap[deltaResponse.getOptionExchangeId()]
    };
    this.getFeedSubject(QUOTE_UPDATE_TYPE.Delta).next(deltaData);
  }

  processUpdatedLtp(quote: any, marketId?: string, securityId?: number) {
    let data: LTPClosePiceObj[] = [];
    const struct: LTPClosePiceObj = {
      market_id: marketId,
      security_id: securityId,
      close_price: quote.closePrice,
      last_trade_price: quote.LTP,
    }
    data.push(struct);
    return data;
  }

  fetchClosePrice(symbolId: number, marketId?: string, securityId?: number) {
    let data: LTPClosePiceObj[] = [];
    // symbols.forEach(el => {
    //     const struct: LTPAndClosePiceObj = {
      //         ExchangeId: el[0],
      //         SecurityId: el[1],
      //         ClosePrice: 0,
      //         LastTradePrice: 0,
      //         PrevClose: 0,
      //         isLtp: false
    //     }
    const struct: LTPClosePiceObj = {
      market_id: marketId,
      security_id: securityId,
      close_price: 0,
      last_trade_price: 0,
      symbol_id: symbolId,
    }
        if (this.symbolSearchService.indicesClosePriceMap.has(Utils.getJoinedString(marketId, securityId))) {
            struct.close_price = this.symbolSearchService.indicesClosePriceMap.get(Utils.getJoinedString(marketId, securityId))[0]
        }
        else if (this.symbolSearchService.symbolInfoMap.has(Utils.getJoinedString(MarketIDMap[marketId], securityId))) {
            /**
             * in symbolinfo map against a key we have array has value, in which
             * index 0 is market lot
             * index 1 is tick size
             * index 2 is scrip precision
             * index 3 is close price
             * index 4 is previous close price
             */
            struct.close_price = this.symbolSearchService.symbolInfoMap.get(Utils.getJoinedString(MarketIDMap[marketId], securityId))[3]
        }
        struct.last_trade_price = struct.close_price;

        data.push(struct)
    

    return data;
}

  /*
    function which will return true or false based on below conditions to update ltp
    if ltp is coming from feed then directly use it
    if ltp is not coming from feed :
    case 1: if current time is before market start time then use close price
    case 2: if current time is after market start time then use ltp
    case 3: if today is holiday then use close price  
  */
  checkToPickClosePrice(exchange: string = EXCHANGE_STRINGS[4], ltp: number) {
    if (ltp) {
      return false;
    } else {
      const config = this.conf.serverConfig;
      const isTodayHoliday = this.conf.checkForHoliday(config.holidays, MarketKeywords[exchange]);
      const confTime = this.timeConfiguration.find(s => s.market == exchange);
      const startTime = moment(confTime.start_time, MOMENT_TIME_FORMAT);
      if ((moment().isBefore(startTime)) || isTodayHoliday) {
        return true;
      } else {
        return false;
      }
    }
  }

  getMapKeyStr(marketID: string, securityId: number) {
    const key = `${marketID}_${securityId}`;
    return key;
  }

  getLTPAndClosePrice(marketId: string, securityId: number) {
    let key: string;
    key = this.getMapKeyStr(marketId, securityId);


    if (!this.ltpAndCloseMap.has(key)) {
      // Initialize with fallback data if needed
      const fallbackData = {
        ExchangeId: marketId,
        SecurityId: securityId,
      } as LTPAndClosePiceObj;
      const symbolKey = `${MarketIDMap[marketId]}_${securityId}`;
      const symbolInfo = Object.assign([], this.symbolSearchService.symbolInfo(symbolKey));
      if (symbolInfo !== null && symbolInfo !== undefined) {
        fallbackData.LastTradePrice = fallbackData.ClosePrice = Utils.applyPrecision(symbolInfo[1], symbolInfo[2]);
      }
      this.ltpAndCloseMap.set(key, fallbackData);
    }

    return this.ltpAndCloseMap.get(key);
  }
}
